import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { triggerInstallPrompt, isStandalone, isMobile, isIOS } from '../utils/pwa';

const InstallPrompt: React.FC = () => {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Don't show if already installed or dismissed
    if (isStandalone() || isDismissed) {
      return;
    }

    // Listen for PWA install availability
    const handleInstallAvailable = () => {
      setShowInstallPrompt(true);
    };

    const handleInstallCompleted = () => {
      setShowInstallPrompt(false);
      setShowIOSInstructions(false);
    };

    window.addEventListener('pwa-install-available', handleInstallAvailable);
    window.addEventListener('pwa-install-completed', handleInstallCompleted);

    // Show iOS instructions if on iOS and mobile
    if (isIOS() && isMobile() && !isStandalone()) {
      const hasSeenIOSInstructions = localStorage.getItem('ios-install-dismissed');
      if (!hasSeenIOSInstructions) {
        setShowIOSInstructions(true);
      }
    }

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('pwa-install-completed', handleInstallCompleted);
    };
  }, [isDismissed]);

  const handleInstall = async () => {
    const installed = await triggerInstallPrompt();
    if (installed) {
      setShowInstallPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    setIsDismissed(true);
  };

  const handleIOSDismiss = () => {
    setShowIOSInstructions(false);
    localStorage.setItem('ios-install-dismissed', 'true');
  };

  if (!isMobile() || isStandalone()) {
    return null;
  }

  return (
    <>
      {/* Android/Chrome Install Prompt */}
      {showInstallPrompt && (
        <div className="fixed bottom-4 left-4 right-4 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 animate-slide-up">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Smartphone className="h-6 w-6 text-[#2B5AB8]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900">
                Install Enneagram App
              </h3>
              <p className="text-xs text-gray-600 mt-1">
                Add to your home screen for quick access and offline use
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-1"
              aria-label="Dismiss install prompt"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex space-x-2 mt-3">
            <button
              onClick={handleInstall}
              className="flex items-center space-x-2 bg-[#2B5AB8] text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-[#224A96] transition-colors flex-1"
            >
              <Download className="h-4 w-4" />
              <span>Install</span>
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      )}

      {/* iOS Install Instructions */}
      {showIOSInstructions && (
        <div className="fixed bottom-4 left-4 right-4 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 animate-slide-up">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Smartphone className="h-6 w-6 text-[#2B5AB8]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900">
                Add to Home Screen
              </h3>
              <div className="text-xs text-gray-600 mt-1 space-y-1">
                <p>1. Tap the Share button in Safari</p>
                <p>2. Scroll down and tap "Add to Home Screen"</p>
                <p>3. Tap "Add" to install the app</p>
              </div>
            </div>
            <button
              onClick={handleIOSDismiss}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-1"
              aria-label="Dismiss install instructions"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex justify-end mt-3">
            <button
              onClick={handleIOSDismiss}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default InstallPrompt;